import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../auth/store';
import { useToast } from '../../../shared/components/toast/useToast';
import {
  shippingZonesApi,
  type CreateShippingZonePayload,
  type ShippingMode,
  type UpdateShippingZonePayload,
} from '../api/shippingZonesApi';

/**
 * Shipping zones & mode (PE-2172/PE-2173).
 *
 * Scoped purely by the `x-tenant-id` header ApiFetch already attaches from
 * `useAuthStore.selectedTenantId` — do NOT pass a merchantId param here (unlike branches).
 *
 * ApiFetch auto-toasts every non-401 error itself (shared/utils/fetch.ts), so mutation
 * `onError` handlers here are no-ops rather than a second toast.
 */

const KEY = 'shipping-zones';
const MODE_KEY = 'shipping-zones-mode';

export const useShippingZones = () => {
  const tenantId = useAuthStore((s) => s.selectedTenantId);
  return useQuery({
    queryKey: [KEY, tenantId],
    queryFn: shippingZonesApi.list,
    enabled: !!tenantId,
  });
};

export const useShippingMode = () => {
  const tenantId = useAuthStore((s) => s.selectedTenantId);
  return useQuery({
    queryKey: [MODE_KEY, tenantId],
    queryFn: shippingZonesApi.getMode,
    enabled: !!tenantId,
  });
};

export const useCreateShippingZone = () => {
  const tenantId = useAuthStore((s) => s.selectedTenantId);
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreateShippingZonePayload) => shippingZonesApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, tenantId] });
      toast.success('Shipping zone created');
    },
    // ApiFetch already toasted the failure — nothing to add here.
    onError: () => {},
  });
};

export const useUpdateShippingZone = () => {
  const tenantId = useAuthStore((s) => s.selectedTenantId);
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateShippingZonePayload }) =>
      shippingZonesApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, tenantId] });
      toast.success('Shipping zone updated');
    },
    onError: () => {},
  });
};

export const useDeleteShippingZone = () => {
  const tenantId = useAuthStore((s) => s.selectedTenantId);
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => shippingZonesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, tenantId] });
      toast.success('Shipping zone deleted');
    },
    onError: () => {},
  });
};

export const useSetShippingMode = () => {
  const tenantId = useAuthStore((s) => s.selectedTenantId);
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (mode: ShippingMode) => shippingZonesApi.setMode(mode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [MODE_KEY, tenantId] });
      toast.success('Shipping mode updated');
    },
    onError: () => {},
  });
};
